import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import store, { Category } from '../Store';

interface CategoryNode {
  id: string;
  name: string;
  name_ar?: string;
  name_de?: string;
  icon_path?: string;
  icon?: {
    emoji?: string;
    unicode?: string;
    type?: string;
    custom_path?: string;
  };
  is_active: boolean;
  num_of_services?: number;
  num_of_children?: number;
  children: CategoryNode[];
  parent_id?: string;
  level: number;
}

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (categoryData: any) => void;
  parentCategories: CategoryNode[];
  selectedParentCategory?: CategoryNode | null;
}

interface EditCategoryModalProps {
  category: CategoryNode | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (categoryData: any) => void;
  parentCategories: CategoryNode[];
}

// Get category name based on current language
const getCategoryName = (category: CategoryNode, currentLanguage: string): string => {
  switch (currentLanguage) {
    case 'ar':
      return category.name_ar || category.name;
    case 'de':
      return category.name_de || category.name_ar || category.name;
    default:
      // For English, try to use the English name, but fallback to Arabic if English is missing
      if (category.name && category.name !== category.name_ar) {
        return category.name;
      }
      // If English name is missing or same as Arabic, show Arabic with a note
      return category.name_ar || category.name;
  }
};

// Get category icon - prioritize API icon, fallback to mapping
const getCategoryIcon = (category: CategoryNode): string => {
  // First check if category has icon data from API
  if (category.icon?.emoji) {
    return category.icon.emoji;
  }
  
  // Fallback to icon mapping based on category name
  const categoryName = category.name;
  const iconMap: { [key: string]: string } = {
    // Main Categories - English
    'Technology': '💻',
    'Design': '🎨',
    'Marketing': '📈',
    'Photography & Video': '📸',
    'Business': '💼',
    'Writing & Translation': '✍️',
    'Education & Training': '🎓',
    'Events': '🎉',
    'Doctors & Pharmacies': '🏥',
    'Lawyers': '⚖️',
    'Translation & Accounting': '📊',
    'Restaurants & Sweets': '🍽️',
    'Shops & Services': '🛍️',
    'Shaving & Beauty': '💄',
    'Cars & Motorcycles': '🚗',
    'Mosques & Churches': '🕌',
    'Real Estate & Building Maintenance': '🏠',
    'Unions, Organizations, Media': '📺',
    'Education & Sports': '⚽',
    
    // Main Categories - Arabic
    'التكنولوجيا': '💻',
    'التصميم': '🎨',
    'التسويق': '📈',
    'التصوير والفيديو': '📸',
    'الأعمال': '💼',
    'الكتابة والترجمة': '✍️',
    'التعليم والتدريب': '🎓',
    'الفعاليات': '🎉',
    'الأطباء والصيدليات': '🏥',
    'المحامين': '⚖️',
    'الترجمة والمحاسبة': '📊',
    'المطاعم والحلويات': '🍽️',
    'المحلات والخدمات': '🛍️',
    'الحلاقة والجمال': '💄',
    'السيارات والدراجات': '🚗',
    'المساجد والكنائس': '🕌',
    'العقارات وصيانة المباني': '🏠',
    'النقابات والمنظمات والإعلام': '📺',
    'التعليم والرياضة': '⚽',
    
    // Technology Subcategories - English
    'Web Development': '🌐',
    'Mobile App Development': '📱',
    'Software Development': '⚙️',
    'IT Support': '🔧',
    
    // Technology Subcategories - Arabic
    'تطوير المواقع': '🌐',
    'تطوير التطبيقات': '📱',
    'تطوير البرمجيات': '⚙️',
    'دعم تقني': '🔧',
    
    // Design Subcategories - English
    'Graphic Design': '🎨',
    'UI/UX Design': '🎯',
    'Interior Design': '🏠',
    'Architecture': '🏗️',
    
    // Design Subcategories - Arabic
    'التصميم الجرافيكي': '🎨',
    'تصميم واجهات المستخدم': '🎯',
    'التصميم الداخلي': '🏠',
    'الهندسة المعمارية': '🏗️',
    
    // Marketing Subcategories - English
    'Digital Marketing': '📊',
    'Social Media Marketing': '📱',
    'SEO Services': '🔍',
    'Content Marketing': '📝',
    
    // Marketing Subcategories - Arabic
    'التسويق الرقمي': '📊',
    'التسويق عبر وسائل التواصل': '📱',
    'خدمات تحسين محركات البحث': '🔍',
    'التسويق بالمحتوى': '📝',
    
    // Photography & Video Subcategories - English
    'Photography': '📷',
    'Videography': '🎬',
    'Video Editing': '✂️',
    'Animation': '🎭',
    
    // Photography & Video Subcategories - Arabic
    'التصوير': '📷',
    'تصوير الفيديو': '🎬',
    'مونتاج الفيديو': '✂️',
    'الرسوم المتحركة': '🎭',
    
    // Business Subcategories - English
    'Business Consulting': '💼',
    'Financial Consulting': '💰',
    'Legal Services': '⚖️',
    'Accounting': '🧮',
    
    // Business Subcategories - Arabic
    'الاستشارات التجارية': '💼',
    'الاستشارات المالية': '💰',
    'الخدمات القانونية': '⚖️',
    'المحاسبة': '🧮',
    
    // Writing & Translation Subcategories - English
    'Content Writing': '✍️',
    'Translation': '🌍',
    'Copywriting': '📝',
    'Technical Writing': '📋',
    
    // Writing & Translation Subcategories - Arabic
    'كتابة المحتوى': '✍️',
    'الترجمة': '🌍',
    'كتابة الإعلانات': '📝',
    'الكتابة التقنية': '📋',
    
    // Education & Training Subcategories - English
    'Online Tutoring': '👨‍🏫',
    'Language Training': '🗣️',
    'Professional Training': '🎯',
    'Corporate Training': '🏢',
    
    // Education & Training Subcategories - Arabic
    'التدريس عبر الإنترنت': '👨‍🏫',
    'تدريب اللغات': '🗣️',
    'التدريب المهني': '🎯',
    'التدريب المؤسسي': '🏢',
    
    // Events Subcategories - English
    'Event Planning': '📅',
    'Wedding Planning': '💒',
    'Corporate Events': '🏢',
    'Catering Services': '🍽️',
    
    // Events Subcategories - Arabic
    'تخطيط الفعاليات': '📅',
    'تخطيط الأعراس': '💒',
    'الفعاليات المؤسسية': '🏢',
    'خدمات التموين': '🍽️',
    
    // Medical Subcategories
    'General & Family Medicine': '👨‍⚕️',
    'Pediatrics': '👶',
    'Internal Medicine': '🫀',
    'Cardiology': '❤️',
    'Cardiac Surgery': '🔪',
    'Vascular Surgery': '🩸',
    'Pulmonology': '🫁',
    'ENT': '👂',
    'Ophthalmology': '👁️',
    'Neurology': '🧠',
    'Neurosurgery': '🔬',
    'Psychiatry': '🧘',
    'General Surgery': '🏥',
    'Plastic Surgery': '✨',
    'Orthopedics': '🦴',
    'Urology': '🔬',
    'Gynecology': '👩',
    'Endocrinology': '⚕️',
    'Dermatology': '🧴',
    'Anesthesiology': '😴',
    'Laboratories': '🧪',
    'Radiology': '📡',
    'Physical Therapy': '🏃',
    'Sterilization': '🧼',
    'Dentistry': '🦷',
    'Pharmacists': '💊',
    
    // Legal Subcategories
    'General Lawyer': '⚖️',
    'Immigration Lawyer': '🛂',
    'Criminal Lawyer': '🚨',
    'Family Lawyer': '👨‍👩‍👧‍👦',
    'Commercial, Real Estate, Labor Lawyer': '📋',
    
    // Translation & Accounting Subcategories
    'Translators': '🌍',
    'Accountants': '🧮',
    'Insurance Agents': '🛡️',
    
    // Restaurant & Sweets Subcategories
    'Restaurants': '🍽️',
    'Cafes': '☕',
    'Sweets': '🍰',
    'Bakeries': '🥖',
    'Butchery': '🥩',
    
    // Shops & Services Subcategories
    'Barber & Beauty': '💇',
    'Mobiles & Computers': '💻',
    'Jewelry': '💍',
    'Clothing & Shoes': '👕',
    'Tailoring': '✂️',
    'Furniture': '🪑',
    'Curtains & Upholstery': '🪟',
    'Kitchens': '🍳',
    'Electrical Appliances & Home Tools': '🔌',
    'Airlines & Tourist Trips': '✈️',
    'Perfumes': '🌸',
    'Eyeglasses': '👓',
    'Libraries/Bookstores': '📚',
    'Flowers & Agriculture': '🌻',
    'Animals': '🐕',
    'Advertising, Marketing & Printing': '📢',
    'Event Halls & Photography': '📸',
    'Suppliers, Wholesalers & Factories': '🏭',
    
    // Beauty Subcategories
    "Men's Barbershop": '💇‍♂️',
    "Women's Hairdresser": '💇‍♀️',
    'Beauty Centers (Laser)': '💄',
    
    // Cars & Motorcycles Subcategories
    'Car Showrooms': '🚗',
    'Car Rental': '🚙',
    'Transportation & Shipping': '🚛',
    'Car Mechanics': '🔧',
    'Car Electric': '⚡',
    'Car Glass': '🪟',
    'Car Upholstery': '🪑',
    'Bodywork & Paint': '🎨',
    'Car Tires': '🛞',
    'Car Spare Parts': '🔩',
    'Decorations & Accessories': '✨',
    'Motorcycle & Scooter Showrooms': '🏍️',
    'Motorcycle & Scooter Maintenance': '🔧',
    'Bicycles, Motorcycles & Scooters': '🚲',
    'Industrial & Agricultural Equipment': '🚜',
    
    // Religious Subcategories
    'Mosques': '🕌',
    'Churches': '⛪',
    
    // Real Estate Subcategories
    'Real Estate Offices': '🏢',
    'Tourist Rental': '🏖️',
    'Construction Companies & Engineers': '👷',
    'Plumbing & Electricity': '🔌',
    'Heaters & Air Conditioners': '❄️',
    'Refrigerator & Washing Machine Maintenance': '🧺',
    'Surveillance Cameras & Alarms': '📹',
    'Solar Energy': '☀️',
    'Painting & Decoration': '🎨',
    'Tiles & Bathroom Installation': '🚿',
    'Carpentry, Blacksmithing, Aluminum': '🔨',
    'Glass': '🪟',
    
    // Organizations Subcategories
    'Unions': '🤝',
    'Civil Society Organizations': '🏛️',
    'Cultural & Artistic Clubs': '🎭',
    'Media & Broadcasting': '📺',
    
    // Education & Sports Subcategories
    'Kindergartens': '🧸',
    'Educational Schools': '🏫',
    'Weekend Institutes': '📚',
    'Sports Clubs': '⚽'
  };
  
  // Try exact match first
  if (iconMap[categoryName]) {
    return iconMap[categoryName];
  }
  
  // Try Arabic name if available
  if (category.name_ar && iconMap[category.name_ar]) {
    return iconMap[category.name_ar];
  }
  
  // Try German name if available
  if (category.name_de && iconMap[category.name_de]) {
    return iconMap[category.name_de];
  }
  
  // Try to find a partial match for common patterns
  const lowerName = categoryName.toLowerCase();
  
  // Medical/Health related
  if (lowerName.includes('طبيب') || lowerName.includes('صيدلية') || lowerName.includes('طب') || lowerName.includes('صحة')) {
    return '🏥';
  }
  
  // Technology related
  if (lowerName.includes('تطوير') || lowerName.includes('برمجة') || lowerName.includes('تقنية') || lowerName.includes('كمبيوتر')) {
    return '💻';
  }
  
  // Design related
  if (lowerName.includes('تصميم') || lowerName.includes('جرافيك') || lowerName.includes('فن')) {
    return '🎨';
  }
  
  // Education related
  if (lowerName.includes('تعليم') || lowerName.includes('تدريب') || lowerName.includes('مدرسة') || lowerName.includes('جامعة')) {
    return '🎓';
  }
  
  // Business related
  if (lowerName.includes('تجارة') || lowerName.includes('استشارات') || lowerName.includes('مالية') || lowerName.includes('محاسبة')) {
    return '💼';
  }
  
  // Food related
  if (lowerName.includes('مطعم') || lowerName.includes('طعام') || lowerName.includes('حلويات') || lowerName.includes('مخبز')) {
    return '🍽️';
  }
  
  // Car related
  if (lowerName.includes('سيارة') || lowerName.includes('دراجة') || lowerName.includes('ميكانيك') || lowerName.includes('نقل')) {
    return '🚗';
  }
  
  // Beauty related
  if (lowerName.includes('جمال') || lowerName.includes('حلاقة') || lowerName.includes('مكياج')) {
    return '💄';
  }
  
  // Legal related
  if (lowerName.includes('محامي') || lowerName.includes('قانون') || lowerName.includes('عدالة')) {
    return '⚖️';
  }
  
  // Real estate related
  if (lowerName.includes('عقار') || lowerName.includes('بناء') || lowerName.includes('إسكان')) {
    return '🏠';
  }
  
  // Sports related
  if (lowerName.includes('رياضة') || lowerName.includes('نادي') || lowerName.includes('لياقة')) {
    return '⚽';
  }
  
  // Default fallback
  return '📁';
};

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  parentCategories,
  selectedParentCategory
}) => {
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    name_de: '',
    parent_id: selectedParentCategory?.id || '',
    is_active: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const categoryData = {
      ...formData,
      parent_id: formData.parent_id || null
    };
    onSave(categoryData);
    setFormData({
      name: '',
      name_ar: '',
      name_de: '',
      parent_id: '',
      is_active: true
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white">
                {selectedParentCategory ? `إضافة فئة فرعية لـ ${selectedParentCategory.name}` : 'إضافة فئة جديدة'}
              </h3>
              <p className="text-blue-100 mt-1">
                {selectedParentCategory ? 'أضف فئة فرعية جديدة' : 'أضف فئة جديدة إلى النظام'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (إنجليزي) *
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                required
                placeholder="أدخل الاسم بالإنجليزية"
              />
            </div>

            <div>
              <label htmlFor="name_ar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (عربي)
              </label>
              <input
                id="name_ar"
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                placeholder="أدخل الاسم بالعربية"
              />
            </div>

            <div>
              <label htmlFor="name_de" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (ألماني)
              </label>
              <input
                id="name_de"
                type="text"
                value={formData.name_de}
                onChange={(e) => setFormData({...formData, name_de: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                placeholder="أدخل الاسم بالألمانية"
              />
            </div>

            <div>
              <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الفئة الرئيسية
              </label>
              <select
                id="parent_id"
                value={formData.parent_id}
                onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
              >
                <option value="">فئة رئيسية</option>
                {parentCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
            />
            <label htmlFor="is_active" className="mr-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              فئة نشطة
            </label>
          </div>

          <div className="flex justify-end space-x-3 space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
            >
              إضافة الفئة
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ 
  category, 
  isOpen, 
  onClose, 
  onSave, 
  parentCategories 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    name_de: '',
    parent_id: '',
    is_active: true
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        name_ar: category.name_ar || '',
        name_de: category.name_de || '',
        parent_id: category.parent_id || '',
        is_active: category.is_active
      });
    }
  }, [category, parentCategories]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const categoryData = {
      ...formData,
      parent_id: formData.parent_id || null
    };
    onSave(categoryData);
  };

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white">تعديل الفئة</h3>
              <p className="text-green-100 mt-1">تعديل بيانات الفئة المحددة</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="edit_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (إنجليزي) *
              </label>
              <input
                id="edit_name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                required
                placeholder="أدخل الاسم بالإنجليزية"
              />
            </div>

            <div>
              <label htmlFor="edit_name_ar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (عربي)
              </label>
              <input
                id="edit_name_ar"
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                placeholder="أدخل الاسم بالعربية"
              />
            </div>

            <div>
              <label htmlFor="edit_name_de" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم (ألماني)
              </label>
              <input
                id="edit_name_de"
                type="text"
                value={formData.name_de}
                onChange={(e) => setFormData({...formData, name_de: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                placeholder="أدخل الاسم بالألمانية"
              />
            </div>

            <div>
              <label htmlFor="edit_parent_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الفئة الرئيسية
              </label>
              <select
                id="edit_parent_id"
                value={formData.parent_id}
                onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
              >
                <option value="">فئة رئيسية</option>
                {parentCategories.filter(cat => cat.id !== category.id).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <input
              type="checkbox"
              id="edit_is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
            />
            <label htmlFor="edit_is_active" className="mr-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              فئة نشطة
            </label>
          </div>

          <div className="flex justify-end space-x-3 space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-teal-600 border border-transparent rounded-lg hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 shadow-lg"
            >
              حفظ التغييرات
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CategoryCard: React.FC<{
  category: CategoryNode;
  onEdit: (category: CategoryNode) => void;
  onDelete: (categoryId: string) => void;
  onToggleExpand: (categoryId: string) => void;
  isExpanded: boolean;
  onEditSubcategory: (subcategory: CategoryNode) => void;
  onDeleteSubcategory: (subcategoryId: string) => void;
  onToggleSubcategory: (subcategoryId: string) => void;
  onToggleMainCategory: (categoryId: string) => void;
  onAddSubcategory: (category: CategoryNode) => void;
}> = ({ category, onEdit, onDelete, onToggleExpand, isExpanded, onEditSubcategory, onDeleteSubcategory, onToggleSubcategory, onToggleMainCategory, onAddSubcategory }) => {
  const { i18n } = useTranslation();
  const hasChildren = category.children && category.children.length > 0;
  const categoryIcon = getCategoryIcon(category);
  const displayName = getCategoryName(category, i18n.language);
  
  

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group`}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 space-x-reverse">
              {hasChildren && (
                <button
                  onClick={() => onToggleExpand(category.id)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-12 h-12 flex items-center justify-center text-3xl">
                  {categoryIcon}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {displayName}
                  </h3>
                  
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(() => {
                      console.log('=== Category Display Debug ===');
                      console.log('Category ID:', category.id);
                      console.log('Category name (English):', category.name);
                      console.log('Category name_ar (Arabic):', category.name_ar);
                      console.log('Category name_de (German):', category.name_de);
                      console.log('Are they different?', category.name !== category.name_ar);
                      console.log('Should show English?', category.name && category.name !== category.name_ar);
                      console.log('================================');
                      return null;
                    })()}
                    {category.name && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                        🇺🇸 {category.name}
                      </span>
                    )}
                    {category.name_ar && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        🇸🇦 {category.name_ar}
                      </span>
                    )}
                    {category.name_de && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        🇩🇪 {category.name_de}
                      </span>
                    )}
                    {!category.name && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                        ⚠️ الاسم الإنجليزي مفقود
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex items-center space-x-4 space-x-reverse">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      category.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      <div className={`w-2 h-2 rounded-full mr-2 ${category.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      {category.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                    
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {category.num_of_services || 0} خدمة
                    </span>
                    
                    {hasChildren && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        {category.children?.length || 0} فئة فرعية
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => onEdit(category)}
              className="p-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 hover:scale-110"
              title="تعديل"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            
            <button
              onClick={() => onToggleMainCategory(category.id)}
              className={`p-3 rounded-lg transition-all duration-200 hover:scale-110 ${
                category.is_active 
                  ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
                  : 'text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20'
              }`}
              title={category.is_active ? 'تعطيل الفئة الرئيسية' : 'تفعيل الفئة الرئيسية'}
            >
              {category.is_active ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </button>
            
            <button
              onClick={() => onDelete(category.id)}
              className="p-3 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 hover:scale-110"
              title="حذف"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Children Categories */}
      {hasChildren && isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 via-blue-50 to-purple-50 dark:from-gray-700 dark:via-blue-900/20 dark:to-purple-900/20">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                    الفئات الفرعية
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {category.children.length} فئة فرعية مرتبطة بـ {category.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <button
                  onClick={() => onAddSubcategory(category)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 transition-colors"
                  title="إضافة فئة فرعية جديدة"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  إضافة فرعية
                </button>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {category.children.length} فئة
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {category.children.map((child) => {
                const childIcon = getCategoryIcon(child);
                return (
                  <div key={child.id} className="group bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-600">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="w-10 h-10 flex items-center justify-center text-2xl">
                          {childIcon}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {getCategoryName(child, i18n.language)}
                          </h5>
                          <div className="mt-1 flex items-center space-x-2 space-x-reverse">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              child.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-1 ${child.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              {child.is_active ? 'نشط' : 'غير نشط'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 space-x-reverse text-sm text-gray-600 dark:text-gray-400">
                        <span className="inline-flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          {child.num_of_services || 0} خدمة
                        </span>
                        {child.num_of_children && child.num_of_children > 0 && (
                          <span className="inline-flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            {child.num_of_children} فرعية
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-1 space-x-reverse opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={() => onEditSubcategory(child)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 hover:scale-110"
                          title="تعديل الفئة الفرعية"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        <button
                          onClick={() => onToggleSubcategory(child.id)}
                          className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                            child.is_active 
                              ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
                              : 'text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20'
                          }`}
                          title={child.is_active ? 'تعطيل الفئة الفرعية' : 'تفعيل الفئة الفرعية'}
                        >
                          {child.is_active ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => onDeleteSubcategory(child.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 hover:scale-110"
                          title="حذف الفئة الفرعية"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {category.children.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">لا توجد فئات فرعية</h4>
                <p className="text-gray-500 dark:text-gray-400">هذه الفئة لا تحتوي على فئات فرعية</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CategoriesPage: React.FC = observer(() => {
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryNode | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<CategoryNode | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    // Force refresh categories to get latest data
    store.fetchCategories();
  }, []);
  
  // Add a refresh button for debugging
  const handleRefresh = () => {
    store.fetchCategories();
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Transform API response to hierarchical structure - only main categories
  const transformToHierarchy = (categories: Category[]): CategoryNode[] => {
    // Use main categories only (parent_id is null/undefined)
    const mainCategories = categories.filter(cat => !cat.parent_id);
    
    return mainCategories.map(cat => ({
      ...cat,
      level: 0,
      children: (cat.children || []).map(child => ({
        ...child,
        level: 1,
        children: []
      }))
    }));
  };

  const hierarchicalCategories = transformToHierarchy(store.categories);

  const filteredCategories = hierarchicalCategories.filter(category => {
    const matchesSearch = category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.name_de?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Also search in children categories
    const childrenMatch = category.children.some(child => 
      child.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      child.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      child.name_de?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return matchesSearch || childrenMatch;
  });

  // Get all subcategories for display
  const allSubcategories = hierarchicalCategories.flatMap(category => category.children);
  
  const filteredSubcategories = allSubcategories.filter(subcategory => {
    const matchesSearch = subcategory.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subcategory.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subcategory.name_de?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });
  

  const handleCreateCategory = async (categoryData: any) => {
    try {
      // If we have a selected category, this is a subcategory
      if (selectedCategory) {
        categoryData.parent_id = selectedCategory.id;
      }
      
      await store.createCategory(categoryData);
      setShowCreateModal(false);
      setSelectedCategory(null);
      showNotification('success', selectedCategory ? 'تم إضافة الفئة الفرعية بنجاح' : 'تم إضافة الفئة بنجاح');
      await store.fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      showNotification('error', 'فشل في إضافة الفئة');
    }
  };

  const handleEditCategory = async (categoryData: any) => {
    const categoryToEdit = selectedCategory || selectedSubcategory;
    if (!categoryToEdit) return;
    
    try {
      await store.updateCategory(categoryToEdit.id, categoryData);
      setShowEditModal(false);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      showNotification('success', 'تم تحديث الفئة بنجاح');
      await store.fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      showNotification('error', 'فشل في تحديث الفئة');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذه الفئة؟ سيتم حذف جميع الفئات الفرعية أيضاً.')) {
      try {
        await store.deleteCategory(categoryId);
        showNotification('success', 'تم حذف الفئة بنجاح');
        await store.fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        showNotification('error', 'فشل في حذف الفئة');
      }
    }
  };

  const handleToggleExpand = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleEdit = (category: CategoryNode) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleEditSubcategory = (subcategory: CategoryNode) => {
    setSelectedCategory(subcategory);
    setSelectedSubcategory(subcategory);
    setShowEditModal(true);
  };

  const handleDeleteSubcategory = async (subcategoryId: string) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذه الفئة الفرعية؟')) {
      try {
        await store.deleteCategory(subcategoryId);
        showNotification('success', 'تم حذف الفئة الفرعية بنجاح');
        await store.fetchCategories();
      } catch (error) {
        console.error('Error deleting subcategory:', error);
        showNotification('error', 'فشل في حذف الفئة الفرعية');
      }
    }
  };

  const handleToggleSubcategory = async (subcategoryId: string) => {
    try {
      // Find the subcategory in the store
      const allCategories = store.categories.slice();
      const subcategory = allCategories.find(cat => cat.id === subcategoryId);
      
      if (subcategory) {
        const newStatus = !subcategory.is_active;
        await store.updateCategory(subcategoryId, { is_active: newStatus });
        showNotification('success', `تم ${newStatus ? 'تفعيل' : 'تعطيل'} الفئة الفرعية بنجاح`);
        await store.fetchCategories();
      }
    } catch (error) {
      console.error('Error toggling subcategory:', error);
      showNotification('error', 'فشل في تغيير حالة الفئة الفرعية');
    }
  };

  const handleToggleMainCategory = async (categoryId: string) => {
    try {
      // Find the main category in the store
      const allCategories = store.categories.slice();
      const category = allCategories.find(cat => cat.id === categoryId);
      
      if (category) {
        const newStatus = !category.is_active;
        await store.updateCategory(categoryId, { is_active: newStatus });
        showNotification('success', `تم ${newStatus ? 'تفعيل' : 'تعطيل'} الفئة الرئيسية بنجاح`);
        await store.fetchCategories();
      }
    } catch (error) {
      console.error('Error toggling main category:', error);
      showNotification('error', 'فشل في تغيير حالة الفئة الرئيسية');
    }
  };

  const handleAddSubcategory = (category: CategoryNode) => {
    setSelectedCategory(category);
    setShowCreateModal(true);
  };

  if (store.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  console.log('CategoriesPage: About to render');

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              إدارة الفئات
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2 text-lg">
              إدارة الفئات الرئيسية والفرعية مع إمكانية التعديل والحذف والإضافة
            </p>
          </div>
          <div className="flex space-x-3 space-x-reverse">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
            >
              🔄 إعادة التحميل
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              إضافة فئة جديدة
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي الفئات</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {store.categories.filter(cat => !cat.parent_id).length + 
                 store.categories.filter(cat => cat.parent_id).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">الفئات النشطة</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {store.categories.filter(cat => cat.is_active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">الفئات الرئيسية</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {store.categories.filter(cat => !cat.parent_id).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي الخدمات</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {store.categories.reduce((sum, cat) => sum + (cat.num_of_services || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">الفئات الفرعية</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {store.categories.filter(cat => cat.parent_id).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 mb-8">
        <div className="max-w-md">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            البحث في الفئات
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              id="search"
              type="text"
              placeholder="البحث في الفئات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-6">
        {filteredCategories.length === 0 && filteredSubcategories.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">لا توجد فئات</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchTerm ? 'لا توجد فئات مطابقة للبحث' : 'ابدأ بإضافة فئة جديدة'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                إضافة فئة جديدة
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Main Categories */}
            {filteredCategories.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  الفئات الرئيسية ({filteredCategories.length})
                </h3>
                <div className="space-y-6">
                  {filteredCategories.map((category) => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      onEdit={handleEdit}
                      onDelete={handleDeleteCategory}
                      onToggleExpand={handleToggleExpand}
                      isExpanded={expandedCategories.has(category.id)}
                      onEditSubcategory={handleEditSubcategory}
                      onDeleteSubcategory={handleDeleteSubcategory}
                      onToggleSubcategory={handleToggleSubcategory}
                      onToggleMainCategory={handleToggleMainCategory}
                      onAddSubcategory={handleAddSubcategory}
                    />
                  ))}
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {/* Modals */}
      <CreateCategoryModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedCategory(null);
        }}
        onSave={handleCreateCategory}
        parentCategories={hierarchicalCategories}
        selectedParentCategory={selectedCategory}
      />

      <EditCategoryModal
        category={selectedCategory || selectedSubcategory}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCategory(null);
          setSelectedSubcategory(null);
        }}
        onSave={handleEditCategory}
        parentCategories={hierarchicalCategories.filter(cat => !cat.parent_id)}
      />

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {notification.type === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            {notification.message}
          </div>
        </div>
      )}
    </div>
  );
});

export default CategoriesPage;